import React from 'react'
import Tooltip from '@mui/material/Tooltip'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import columns from '../../UI/VerticalTable/columns'
import { COLUMNS_IDS } from 'UI/VerticalTable/utils'
import { removeKeywordSuffix } from 'containers/VerticalPage/common/utils'
import { ITableColumnsType } from 'uikit/tables/default'
import { NOT_AVAILABLE } from 'utils/constants'
import BusinessDataSources from 'components/BusinessDataSources'

/** Paraguay RUC: 6-8 digits, hyphen, 1 check digit */
export const isValidRUC = (val: string | undefined | null): boolean =>
  !!val && /^\d{6,8}-\d$/.test(val.trim())

const revenueTooltipSx = {
  '&.MuiTooltip-tooltip': {
    maxWidth: 340, fontSize: '16px !important', lineHeight: '24px !important', color: 'rgba(0,0,0,0.85)',
    backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px',
    padding: '14px 18px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
}
const revenueTooltipArrowSx = { color: '#fff', '&::before': { border: '1px solid #d9d9d9' } }
const revenueTooltipContent = React.createElement('span', null,
  React.createElement('b', null, 'Estimated'),
  ' range based on two independent methods: review-based analysis and market comparison.',
)

export const estimatedRevenueColumn = {
  title: 'Est. Revenue',
  key: 'estimatedRevenue',
  width: undefined,
  sorter: false,
  render: (item: any) => {
    const low = item?.totals?.estimatedRevenueLow
    const high = item?.totals?.estimatedRevenueHigh
    if (low == null && high == null) return NOT_AVAILABLE
    const text = `$${Math.round(low || 0).toLocaleString()} – $${Math.round(high || 0).toLocaleString()}`
    return React.createElement(Tooltip, {
      title: revenueTooltipContent,
      arrow: true,
      placement: 'top' as const,
      slotProps: { tooltip: { sx: revenueTooltipSx }, arrow: { sx: revenueTooltipArrowSx } },
    }, React.createElement('span', { style: { cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: '4px' } },
      text,
      React.createElement(InfoOutlinedIcon, { sx: { fontSize: '14px', color: 'rgba(0,0,0,0.3)' } }),
    ))
  },
}

export const strColumns: ITableColumnsType<any> = [
  {
    ...columns[COLUMNS_IDS.hostName],
    title: 'Host Profile',
    width: undefined,
    render: item => {
      const name = item?.totals?.hostName
      if (!name) return NOT_AVAILABLE
      return React.createElement('span', { style: { color: '#1890ff' } }, name)
    },
  },
  estimatedRevenueColumn as any,
  {
    ...columns[COLUMNS_IDS.tin],
    title: 'TIN',
    width: undefined,
    render: item => {
      const tin = item?.entityDetails?.tin
      if (!isValidRUC(tin)) return NOT_AVAILABLE
      return tin
    },
  },
  { ...columns[COLUMNS_IDS.verifiedName], width: undefined },
  {
    ...columns[COLUMNS_IDS.activeListings],
    width: undefined,
    render: item => {
      const val = item?.totals?.activeListings
      if (typeof val !== 'number') return NOT_AVAILABLE
      return React.createElement(Tooltip, {
        title: React.createElement('span', null, 'Number of listings currently displayed for this host, excluding removed or non-Paraguay listings.'),
        arrow: true,
        placement: 'top' as const,
        slotProps: { tooltip: { sx: revenueTooltipSx }, arrow: { sx: revenueTooltipArrowSx } },
      }, React.createElement('span', { style: { cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: '4px' } },
        val.toLocaleString(),
        React.createElement(InfoOutlinedIcon, { sx: { fontSize: '14px', color: 'rgba(0,0,0,0.3)' } }),
      ))
    },
  },
  {
    ...columns[COLUMNS_IDS.numberOfReviews],
    width: undefined,
    render: item => {
      const val = item?.totals?.numberOfReviews
      if (typeof val !== 'number') return NOT_AVAILABLE
      return React.createElement(Tooltip, {
        title: React.createElement('span', null, 'Total reviews across this host\u2019s displayed listings only, not all reviews for the host.'),
        arrow: true,
        placement: 'top' as const,
        slotProps: { tooltip: { sx: revenueTooltipSx }, arrow: { sx: revenueTooltipArrowSx } },
      }, React.createElement('span', { style: { cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: '4px' } },
        val.toLocaleString(),
        React.createElement(InfoOutlinedIcon, { sx: { fontSize: '14px', color: 'rgba(0,0,0,0.3)' } }),
      ))
    },
  },
  {
    ...columns[COLUMNS_IDS.locations],
    title: 'Host Address',
    width: undefined,
    render: item => {
      const addr = item?.contactInfo?.addresses
      if (addr && addr.length > 0) return addr[0]
      return item?.entityDetails?.location || NOT_AVAILABLE
    },
  },
  {
    ...columns[COLUMNS_IDS.dataSources],
    title: 'Host Verifications',
    width: undefined,
    render: item => {
      const weblinks = item?.contactInfo?.weblinks || {}
      const pairs: { platform: string; url: string }[] = []
      const seen = new Set<string>()

      // Normalize weblink keys to match BusinessSource allowedSources
      const normalizeSource = (key: string): string => {
        const k = key.toLowerCase().replace(/_profile$/, '')
        return k
      }

      // Revenue sources (e.g. Airbnb listings)
      item?.revenueSources?.forEach(rs => {
        const p = rs?.platform?.toLowerCase()
        if (p && !seen.has(p)) {
          seen.add(p)
          pairs.push({ platform: p, url: rs?.profileUrl || '' })
        }
      })

      // Weblinks (social profiles, other sites)
      Object.entries(weblinks).forEach(([source, linkArray]: [string, any]) => {
        const p = normalizeSource(source)
        if (!seen.has(p)) {
          seen.add(p)
          const url = Array.isArray(linkArray) && linkArray.length > 0 ? linkArray[0] : ''
          pairs.push({ platform: p, url })
        }
      })

      pairs.sort((a, b) => a.platform.localeCompare(b.platform))

      return React.createElement(BusinessDataSources, {
        dataSources: pairs.map(p => p.platform),
        urls: pairs.map(p => p.url),
      })
    },
  },
]

export const keysForEmptyColumnsRequest = removeKeywordSuffix(
  strColumns.map(el => `${el.key}` || '').filter(key => Boolean(key)),
)
